import { Button, Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Desc, Title } from "../../../components/Desc";
import ProgressIndicator from "../../../components/ProgressIndicator";
import strings from "../../../config/localization";
import { useAutoResourceAllocationsMutation } from "../../../services/appln";
import { useGetResourceAssessmentsOfTimeslotQuery } from "../../../services/ResourceAssessmentApi";
import { getLocationFilters } from "../../util";

interface DataType {
  key: string;
  locationId: string;
  name: string;
  kind: string;
  parent: string;
  resourceTypeId: string;
  resourceTypeName: string;
  assessmentCount: number;
  allocatedCount: number;
  status: string;
}

interface Data {
  rowData: DataType[];
  columnData: ColumnsType<DataType>;
}

export default function BandobastTimeslotResAllocationSum() {
  console.log("BandobastTimeslotResAllocationSum");
  const params = useParams();
  console.log("BandobastTimeslotResAllocationSum, params:", params);
  const navigate = useNavigate();

  const bandobastContext = JSON.parse(localStorage.getItem("bandobast")!);
  const timeslotContext = JSON.parse(localStorage.getItem("timeslot")!);

  const [autoAllocate, autoAllocateParams] =
    useAutoResourceAllocationsMutation();

  const [tableData, setTableData] = React.useState<Data>({
    rowData: [],
    columnData: [],
  });

  const assessmentsQueryResponse = useGetResourceAssessmentsOfTimeslotQuery({
    bandobastId: params.bandobastId,
    timeslotId: params.timeslotId,
  });

  React.useEffect(() => {
    console.log(
      "BandobastTimeslotResAllocationSum, response:",
      assessmentsQueryResponse,
    );
    if (!assessmentsQueryResponse.isSuccess) {
      return;
    }

    const rowData = assessmentsQueryResponse.data.data.map((e: any) => {
      const key = `${e.locationId}:${e.resourceTypeId}`;
      return {
        key: key,
        locationId: e.locationId,
        resourceTypeId: e.resourceTypeId,
        name: e.locationName,
        kind: e.locationKind,
        parent: "",
        resourceTypeName: e.resourceTypeName,
        assessmentCount: e.count,
        allocationCount: e.allocationCount,
        status:
          e.count === e.allocationCount ? strings.allocated : strings.pending,
      };
    });

    const resourceTypeSet = new Set<string>(
      rowData.map((e: DataType) => e.resourceTypeName),
    );

    const columns: ColumnsType<DataType> = [
      {
        title: strings.name,
        dataIndex: "name",
        key: "name",
      },
      {
        title: strings.kind,
        dataIndex: "kind",
        key: "kind",
        filters: getLocationFilters(),
        onFilter: (value: any, record) => {
          return record.kind === (value as string);
        },
      },
      {
        title: strings.parent,
        dataIndex: "parent",
        key: "parent",
      },
      {
        title: strings.resourceType,
        dataIndex: "resourceTypeName",
        key: "resourceTypeName",
        // filters: Array.from(resourceType.keys())
        //   .sort()
        //   .map((e) => {
        //     return { text: e, value: e };
        //   }),
        // onFilter: (value: any, record) => {
        //   return record.designationName === (value as string);
        // },
      },
      {
        title: strings.assessmentCount,
        dataIndex: "assessmentCount",
        key: "assessmentCount",
        render: (text, record, index) => (
          <Button
            onClick={() => {
              navigate(
                `../resourceAssessments/${params.bandobastId}/${record.locationId}`,
              );
            }}
          >
            {text as string}
          </Button>
        ),
      },
      {
        title: strings.allocatedCount,
        dataIndex: "allocationCount",
        key: "allocationCount",
        render: (text, record, index) => {
          console.log("record", record);
          return (
            <Button
              onClick={() => {
                const toStore = {
                  id: record.locationId,
                  name: record.name,
                  resourceTypeId: record.resourceTypeId,
                  resourceTypeName: record.resourceTypeName,
                  count: record.assessmentCount,
                };
                localStorage.setItem("location", JSON.stringify(toStore));
                navigate(
                  `../resourceAllocation/${params.bandobastId}/${params.timeslotId}/${record.locationId}/${record.resourceTypeId}`,
                );
              }}
            >
              {text as string}
            </Button>
          );
        },
      },
      {
        title: strings.status,
        dataIndex: "status",
        key: "status",
        filters: [strings.allocated, strings.pending].sort().map((e) => {
          return { text: e, value: e };
        }),
        onFilter: (value: any, record) => {
          return record.status === (value as string);
        },
      },
    ];
    setTableData({
      rowData: rowData,
      columnData: columns,
    });
  }, [assessmentsQueryResponse]);

  console.log("tableData", tableData);

  return (
    <Box sx={{ flexGrow: 1, width: "100%" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Title text={strings.resourceAllocationSummary} />
        </Grid>
        {(assessmentsQueryResponse.isLoading ||
          assessmentsQueryResponse.isFetching ||
          autoAllocateParams.isLoading) && <ProgressIndicator />}

        <>
          <Grid item xs={12}>
            <Stack spacing={0}>
              <Desc label={strings.bandobast} value={bandobastContext.name} />
              <Desc label={strings.timeslot} value={timeslotContext.name} />
              <Desc
                label={strings.startTime}
                value={timeslotContext.startTime}
              />
              <Desc label={strings.endTime} value={timeslotContext.endTime} />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              id={"autoAllocate"}
              onClick={() => {
                autoAllocate({
                  bandobastId: params.bandobastId,
                  timeslotId: params.timeslotId,
                });
              }}
            >
              {strings.allocate}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Table
              virtual
              scroll={{ y: 600 }}
              columns={tableData.columnData}
              dataSource={tableData.rowData}
              pagination={false}
            />
          </Grid>
        </>
      </Grid>
    </Box>
  );
}
